import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Box,
  OptionSelect,
  Switch,
  resolveMotionPreference,
} from "@salestrak/ui";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import { useUpdatePreferences, useUserSettings } from "#app/lib/hooks/use-user-settings";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { TeamSelector } from "#app/components/entity-selectors";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { PREFERENCES_ANCHORS } from "#app/components/settings-search/rows/preferences";

import { DEFAULT_ROUTE_OPTIONS, DEFAULT_ROUTE_VALUE } from "#app/lib/default-route";
// The week-start choices live with the provider that honors them, so the
// Select here and the calendars can never drift apart.
import {
  TIME_FORMAT_OPTIONS,
  DEFAULT_TIME_FORMAT,
  WEEK_START_OPTIONS,
  resolveHourCycle,
  resolveWeekStart,
} from "#app/components/app-calendar-provider";
// The toast anchors live with the viewport that honors them, so the Select
// here and the provider can never drift apart.
import {
  DEFAULT_TOAST_POSITION,
  TOAST_POSITION_OPTIONS,
  previewToastPosition,
  resolveToastPosition,
} from "#app/components/app-toast-provider";
// The motion choices live with the effect that honors them, for the same
// reason.
import { REDUCE_MOTION_OPTIONS } from "#app/components/app-interaction-preferences";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";


// The UI is a two-option Select (reference parity); storage stays the
// boolean `displayFullNames` preference these values map onto.
const DISPLAY_NAME_OPTIONS = [
  { label: "Username", value: "username" },
  { label: "Full name", value: "full-name" },
];

// Stored preferences travel as untyped strings (null while unloaded,
// arbitrary if stale) — resolve to a known option value or the fallback.
function resolveValue(
  options: readonly { value: string; label: string }[],
  val: string | null | undefined,
  fallback: string,
): string {
  return options.some((o) => o.value === val) ? (val as string) : fallback;
}

function PreferencesPage() {
  // No preferences row yet (new user) simply means every field reads null and
  // each Select resolves to its own fallback — there is no need to fabricate a
  // whole default row to stand in for one.
  const { preferences, isLoading, error, refetch, updatePreference } =
    useUserSettings();
  const updatePreferences = useUpdatePreferences();
  // Default teams only mean something to a member of several: one team is
  // assigned without asking. The picker offers only the viewer's own teams.
  const { teams, teamIds: viewerTeamIds } = useWorkspaceData();
  const otherTeamIds = React.useMemo(
    () => teams.filter((t) => !viewerTeamIds.includes(t.teamId)).map((t) => t.teamId),
    [teams, viewerTeamIds],
  );
  const autoAssignTeam = preferences?.autoAssignTeam ?? true;
  const showDefaultTeams = autoAssignTeam && viewerTeamIds.length > 1;
  const defaultTeamIds = React.useMemo(
    () => (preferences?.defaultTeamIds ?? []).filter((id) => viewerTeamIds.includes(id)),
    [preferences?.defaultTeamIds, viewerTeamIds],
  );
  // Lives in the generic settings bag, not the preferences row — the same
  // switch the banner picker's "Hide banners" flips.

  if (isLoading && !preferences) {
    return (
      <Box fill="flex" overflow="auto">
        <PageHeader centered>
          <PageHeaderTitle>Preferences</PageHeaderTitle>
        </PageHeader>
        {/* Mirrors the settled run: General (3 rows), Interface (5 rows),
          * Assignment (2 rows) — so the resolve lands with no reflow above
          * the fold. */}
        <SettingsPageSkeleton
          groups={[
            { rows: 3 },
            { rows: 5 },
            { rows: 2 },
          ]}
        />
      </Box>
    );
  }

  if (error && !preferences) {
    return <LoadErrorState noun="preferences" error={error} onRetry={() => { void refetch(); }} />;
  }

  return (
    <Box fill="flex" overflow="auto">
      <PageHeader centered>
        <PageHeaderTitle>Preferences</PageHeaderTitle>
      </PageHeader>
      <Box direction="column" align="center" paddingY="md" paddingX="md">
        <Box direction="column" gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
          <SettingsGroup label="General">
            <SettingsGroupRow
              label="Default view"
              highlightAnchor={PREFERENCES_ANCHORS.defaultView}
              subtitle="Where SalesTrak lands when it opens"
            >
              <OptionSelect
                aria-label="Default view"
                onValueChange={(value) => updatePreference("defaultRoute", value)}
                options={DEFAULT_ROUTE_OPTIONS}
                value={resolveValue(
                  DEFAULT_ROUTE_OPTIONS,
                  preferences?.defaultRoute,
                  DEFAULT_ROUTE_VALUE,
                )}
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Display names"
              highlightAnchor={PREFERENCES_ANCHORS.displayNames}
              subtitle="How Member names display"
            >
              <OptionSelect
                aria-label="Display names"
                onValueChange={(value) =>
                  updatePreference("displayFullNames", value === "full-name")
                }
                options={DISPLAY_NAME_OPTIONS}
                value={preferences?.displayFullNames ? "full-name" : "username"}
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="First day of the week"
              highlightAnchor={PREFERENCES_ANCHORS.firstDayOfWeek}
              subtitle="Which day the calendar week starts on"
            >
              <OptionSelect
                aria-label="First day of the week"
                onValueChange={(value) =>
                  updatePreference("firstDayOfWeek", Number(value))
                }
                options={WEEK_START_OPTIONS}
                value={String(resolveWeekStart(preferences?.firstDayOfWeek))}
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Time format"
              highlightAnchor={PREFERENCES_ANCHORS.timeFormat}
              subtitle="Show times on a 12-hour or 24-hour clock"
            >
              <OptionSelect
                aria-label="Time format"
                onValueChange={(value) => updatePreference("timeFormat", value)}
                options={TIME_FORMAT_OPTIONS}
                value={
                  resolveHourCycle(preferences?.timeFormat) === 24
                    ? "24h"
                    : DEFAULT_TIME_FORMAT
                }
              />
            </SettingsGroupRow>
          </SettingsGroup>

          <SettingsGroup label="Interface">
            {/* No Font size row: the stored `fontSize` preference has one
                value and nothing reads it, so a picker would offer a choice
                that changes nothing. It returns with a second size. */}
            <SettingsGroupRow
              label="Toast position"
              highlightAnchor={PREFERENCES_ANCHORS.toastPosition}
              subtitle="Where notifications appear on screen"
            >
              <OptionSelect
                aria-label="Toast position"
                onValueChange={(value) => {
                  void updatePreference("toastPosition", value);
                  // Preview at the NEW position: the optimistic write has
                  // already moved the viewport by the time this renders.
                  previewToastPosition(resolveToastPosition(value));
                }}
                options={TOAST_POSITION_OPTIONS}
                value={resolveValue(
                  TOAST_POSITION_OPTIONS,
                  preferences?.toastPosition,
                  DEFAULT_TOAST_POSITION,
                )}
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Use pointer cursors"
              highlightAnchor={PREFERENCES_ANCHORS.pointerCursors}
              subtitle="Show a pointer cursor over anything pressable"
            >
              {/* null = never set, which is ON — the shipped default. */}
              <Switch
                aria-label="Use pointer cursors"
                checked={preferences?.pointerCursors ?? true}
                onCheckedChange={(v) =>
                  updatePreference("pointerCursors", v)
                }
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Show unread count as a dot"
              highlightAnchor={PREFERENCES_ANCHORS.railCountDot}
              subtitle="Replace the number on the navigation's Home tile with a dot"
            >
              <Switch
                aria-label="Show unread count as a dot"
                checked={preferences?.railCountDot ?? false}
                onCheckedChange={(v) => updatePreference("railCountDot", v)}
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Show empty properties"
              highlightAnchor={PREFERENCES_ANCHORS.showEmptyProperties}
              subtitle="Open a Project with its blank properties shown instead of folded behind More"
            >
              {/* null = never set, which is OFF — a blank row is not information. */}
              <Switch
                aria-label="Show empty properties"
                checked={preferences?.showEmptyProperties ?? false}
                onCheckedChange={(v) => updatePreference("showEmptyProperties", v)}
              />
            </SettingsGroupRow>

            <SettingsGroupRow
              label="Reduce motion"
              highlightAnchor={PREFERENCES_ANCHORS.reduceMotion}
              subtitle="Reduce animations or match your system"
            >
              <OptionSelect
                aria-label="Reduce motion"
                onValueChange={(value) =>
                  updatePreference("reduceMotion", value)
                }
                options={REDUCE_MOTION_OPTIONS}
                value={resolveMotionPreference(preferences?.reduceMotion)}
              />
            </SettingsGroupRow>
          </SettingsGroup>

          {/* "Assignment", not "Projects": accounts and contacts will carry
              the same assignment behaviors once those models exist. */}
          <SettingsGroup label="Assignment">
            <SettingsGroupRow
              label="Auto-assign to self"
              highlightAnchor={PREFERENCES_ANCHORS.autoAssignSelf}
              subtitle="Automatically assign new projects you create to yourself"
            >
              {/* null = never set, which is ON — the shipped default. */}
              <Switch
                aria-label="Auto-assign to self"
                checked={preferences?.autoAssignSelf ?? true}
                onCheckedChange={(v) => updatePreference("autoAssignSelf", v)}
              />
            </SettingsGroupRow>
            <SettingsGroupRow
              label="Auto-assign my team"
              highlightAnchor={PREFERENCES_ANCHORS.autoAssignTeam}
              subtitle="Automatically assign your team to new projects you create"
            >
              {/* null = never set, which is ON — the shipped default. */}
              <Switch
                aria-label="Auto-assign my team"
                checked={autoAssignTeam}
                onCheckedChange={(v) => updatePreference("autoAssignTeam", v)}
              />
            </SettingsGroupRow>
            {showDefaultTeams ? (
              <SettingsGroupRow
                label="Default teams"
                subtitle="Your teams to assign to new projects you create"
              >
                {/* Wears the Select field's dress so it is indistinguishable
                    from the selects above it. The value rule: one team reads
                    as tile + name, two or more collapse to the avatar stack
                    (never a "+N" pill); none reads "—". The popup is the
                    viewer's own teams alone — a short, known list needs no
                    search field or Selected band. */}
                <TeamSelector
                  value={defaultTeamIds}
                  onValueChange={(ids) => { void updatePreferences({ defaultTeamIds: ids }); }}
                  exclude={otherTeamIds}
                  aria-label="Default teams"
                  field
                  search={false}
                />
              </SettingsGroupRow>
            ) : null}
          </SettingsGroup>

          <SettingsPageGutter />
        </Box>
      </Box>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/preferences")({
  component: PreferencesPage,
});
